import { dedupeUiMessages, getToolCallIds } from "./chat.helpers";
import type { ExtendedUIMessage } from "./chat.types";
import { getRenderKey } from "./render-identity";

/**
 * The ephemeral streaming slot (brief 011, PR 5).
 *
 * On the server-authoritative render path, persisted `serverHistory` is the base
 * list and any in-flight `useChat` turns render in a separate transient region —
 * the slot — pinned at the conversation tail. A turn streams live in the slot,
 * then moves into `serverHistory` once the server has durably captured it. The
 * only live↔server touch is the single id-presence check below; there is no
 * interleaved merge, overlay-precedence table, or `renderKey` collapse.
 */

/** The minimum a server-history row must expose for the drop predicate. */
type ServerHistoryRow = Pick<ExtendedUIMessage, "externalMessageId">;

/**
 * Decides, **per turn**, whether a live (SDK) turn may leave the ephemeral slot
 * because server history has durably captured it: drop it iff `serverHistory`
 * contains **exactly one** row whose `externalMessageId` equals the turn's id.
 *
 * The decision is purely id-presence — deliberately NOT gated on whether the SDK is
 * idle. A turn only appears in `serverHistory` once it is persisted, so:
 *
 * - **A still-streaming turn** isn't persisted yet ⇒ zero matches ⇒ stays in the
 *   slot, rendering live tokens. No idle gate is needed to protect it.
 * - **A completed, persisted turn** ⇒ one match ⇒ drops from the slot and renders
 *   from the base — *even while a later turn is streaming*. Gating on a global
 *   "SDK idle" would instead keep every already-persisted prior turn in the slot
 *   for the duration of any new stream, rendering each one twice (once from base,
 *   once from the slot). Per-turn id-presence is what prevents that duplication.
 * - **Zero matches**: keep. Covers the finish→refetch gap (row not landed yet — a
 *   later refetch drops it) AND terminal turns whose row class server history
 *   excludes — an aborted/stopped turn (PR 4 drops aborted rows) or a persist-
 *   failed/unsynced turn (may never persist) has no rendered row, so it lingers as
 *   a terminal/error overlay until the user clears it or the session switches (the
 *   slot lifecycle owns that exit, not this predicate).
 * - **More than one match**: keep (ambiguity-safe). `external_message_id` is unique
 *   only within a session, and `serverHistory` is a root+child *merged* list, so a
 *   bare id can in principle hit rows in more than one session (legacy synthetic ids
 *   such as `user-1`/empty strings span thousands of sessions in prod). An ambiguous
 *   match is never grounds for an arbitrary drop. Streamed assistant ids (`aisdk-…`)
 *   are collision-free across sessions, so the turns that pass through the slot
 *   resolve to exactly one row.
 *
 * A falsy `liveTurn.id` never matches (guards an empty stream id against legacy
 * empty-string `external_message_id` rows).
 */
export const canDropTurnFromSlot = (liveTurn: { id: string }, serverHistory: ServerHistoryRow[]): boolean => {
  if (!liveTurn.id) {
    return false;
  }

  let matches = 0;
  for (const row of serverHistory) {
    if (row.externalMessageId && row.externalMessageId === liveTurn.id) {
      matches += 1;
      if (matches > 1) {
        // Ambiguous — a session-unique id matched rows in more than one session.
        // Fail safe: keep the turn in the slot rather than drop against an
        // arbitrary match.
        return false;
      }
    }
  }

  return matches === 1;
};

/**
 * Composes the server-authoritative render list: persisted `serverHistory` as the
 * base, followed by the ephemeral slot — the live SDK turns whose persisted row is
 * not yet in history (per `canDropTurnFromSlot`). `liveTurns` is the SDK's turns
 * already stripped of the injected system message; each is kept in the slot only
 * until its own row lands in `serverHistory`.
 *
 * The settled turns are run through `dedupeUiMessages` — an id + tool-call collapse.
 * `canDropTurnFromSlot` handles the normal base↔slot handoff by id, but a
 * multi-frame orchestration turn (B-1416) can surface the same frame in both lists — e.g. the
 * SDK re-emitting the foreground frame when a nested child frame opens a second message in one
 * response. Deduping folds such a turn to its base copy (base is first, so the persisted copy
 * wins) while distinct child turns (different id AND tool-call ids) all survive.
 *
 * `streamingTurn` is the live turn OBJECT the SDK is actively streaming — the caller passes the
 * `liveTurns` tail ONLY while `status === "streaming"` (never `submitted`, where nothing has
 * streamed yet, so a merely-submitted turn keeps its canonical persisted row). That turn's LIVE
 * copy must win over any stale/partial persisted twin — its tool output can persist into
 * `serverHistory` mid-stream (the history-notifier wakeup, B-1406/#13475), so a plain dedup
 * (base-first) would fold the live turn onto that stale row and blank the stream. So we drop the
 * streaming turn's twin from the base, then run the WHOLE list through the same `dedupeUiMessages`
 * the idle path uses. (Before B-1445 this path also caught the elicit-resume continuation, which
 * reused the persisted elicit message's id under the suppressed `start`; on the flag-on path the
 * continuation now streams under its own `aisdk-` id, so that id-collision no longer occurs and the
 * twin-drop serves the mid-stream tool-output-persist case.)
 *
 * The list is ALWAYS fully deduped — duplicates collapse to one, no exemption. Matching the
 * streaming turn by object REFERENCE (not an id-wide predicate) means a stale same-id replay copy
 * earlier in `liveTurns` is dropped, so the first-wins dedupe can never pick it over the growing
 * tail (B-1443). If the caller points at the wrong object the worst case is briefly showing that
 * copy — never a duplicate. When nothing is streaming, the list is deduped unchanged, identical to
 * the idle path.
 */
export const composeSlotRenderMessages = <
  T extends {
    id: string;
    externalMessageId?: string | null;
    renderKey?: string | null;
    parts?: ExtendedUIMessage["parts"];
  },
>(
  serverHistory: T[],
  liveTurns: T[],
  streamingTurn?: T | null
): T[] => {
  // The actively-streaming turn is NEVER dropped from the slot, even when its id already matches a
  // serverHistory row (e.g. B-1406's mid-stream tool-output persist lands a twin row under the
  // streaming id, so `canDropTurnFromSlot` would drop it). Its stale persisted twin is removed from
  // the base below (twin-drop) instead. Matching by object reference also drops any OTHER live copy
  // sharing the streaming id (a stale replay), so only this exact tail survives (B-1443).
  const slotTurns = liveTurns.filter((turn) => {
    if (streamingTurn && turn.id === streamingTurn.id) {
      return turn === streamingTurn;
    }
    return !canDropTurnFromSlot(turn, serverHistory);
  });

  if (!streamingTurn) {
    return dedupeUiMessages([...serverHistory, ...slotTurns]);
  }

  // Keys that identify the streaming turn's own (partial) persisted twin: its stream id — which is
  // the persisted `externalMessageId` — and its tool-call ids (model-generated, unique per call).
  // The streaming turn's parts are the caller's already-`streamPartOffset`-sliced OWN parts (B-1454),
  // so a prior frame's inherited tool-call id is not here to wrongly twin-drop that frame's own row.
  const streamingKeys = new Set<string>([streamingTurn.id, ...getToolCallIds(streamingTurn.parts)]);
  const base = serverHistory.filter((row) => {
    if (row.externalMessageId && streamingKeys.has(row.externalMessageId)) {
      return false;
    }
    return !getToolCallIds(row.parts).some((toolCallId) => streamingKeys.has(toolCallId));
  });
  return dedupeUiMessages([...base, ...slotTurns]);
};

/**
 * The clean-seed eviction selector (B-1445 S3).
 *
 * On the server-authoritative path `useChat.messages` is a transient live-turn buffer, not the
 * render source. Once a turn is durably in `serverHistory` (same predicate as the slot handoff,
 * `canDropTurnFromSlot`) it can leave the buffer: `renderKey` identity (S2) makes the eviction
 * gap-free — the evicted live turn and its persisted row share a render key, so the render list is
 * unchanged. Evicting matters because `ai@6`'s `createStreamingUIMessageState` seeds a resumed
 * stream by CLONING the last message iff its role is `assistant`; keeping a settled elicit turn as
 * the tail makes the elicit-resume continuation seed clone its `tool-elicit` part (whose shared
 * toolCallId then makes dedupe drop the continuation). Evicting the elicit turn leaves a
 * non-assistant tail ⇒ a fresh, clean continuation seed.
 *
 * `messages[0]` is the injected system message and is always retained. Returns the retained list to
 * hand to `setMessages`, or `null` when nothing is evictable — so the caller never churns
 * `setMessages` in a render loop.
 */
export const selectMessagesAfterEviction = <T extends { id: string }>(
  messages: T[],
  serverHistory: ServerHistoryRow[]
): T[] | null => {
  const retained = messages.filter((message, index) => index === 0 || !canDropTurnFromSlot(message, serverHistory));
  return retained.length === messages.length ? null : retained;
};

/**
 * Synchronous clean-seed for the elicit-resume submit path (B-1445 S3, refetch-race hardening).
 *
 * The effect-based `selectMessagesAfterEviction` can only evict the settled elicit turn once it is in
 * `serverHistory`, but the SDK goes idle BEFORE the post-settlement history refetch lands. A user who
 * submits in that window leaves the `tool-elicit` turn at the `useChat` tail, so `ai@6` seeds the
 * resume by cloning it (its shared `toolCallId` then makes render-dedupe drop the continuation — the
 * exact regression S3 prevents). Calling this synchronously in `submitElicitationResponse` right
 * before `resumeStream`, keyed by the resolve's exact `toolCallId`, drops that turn from the buffer
 * so the clean seed holds by construction rather than depending on effect/refetch timing.
 *
 * Returns the same array reference when no turn carries the toolCallId (already evicted), so a
 * functional `setMessages` updater is a no-op in that case.
 */
export const selectMessagesAfterResolvingElicit = <T extends { id: string; parts?: ExtendedUIMessage["parts"] }>(
  messages: T[],
  resolvedToolCallId: string
): T[] => {
  const filtered = messages.filter((message) => !getToolCallIds(message.parts).includes(resolvedToolCallId));
  return filtered.length === messages.length ? messages : filtered;
};

/**
 * Prunes the render-only held-turns area (B-1445).
 *
 * When the elicit-resume submit drops the settled elicit turn from the `useChat` buffer for a clean
 * seed, it first parks that turn here so the render keeps showing it during the settle→history-refetch
 * window (otherwise removing the only rendered copy blanks the bubble). A held turn is released the
 * moment its authoritative row lands in `serverHistory` — matched by `renderKey`, so the handoff is
 * gap-free (the held copy and the persisted row are the same keyed element). Returns the same array
 * reference when nothing is pruned so the caller never churns state.
 */
export const pruneHeldTurns = <T extends { id: string; renderKey?: string | null }>(
  held: T[],
  serverHistory: readonly { id: string; renderKey?: string | null }[]
): T[] => {
  if (held.length === 0) {
    return held;
  }
  const historyKeys = new Set(serverHistory.map(getRenderKey));
  const remaining = held.filter((turn) => !historyKeys.has(getRenderKey(turn)));
  return remaining.length === held.length ? held : remaining;
};
